import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { yiluEnv } from 'utils';
import { decodeBase64 } from 'utils/base64Utils';
import { configureLocale } from 'utils/i18n';

import routes from 'router/routes';

import { EmbeddedEventType, EmbeddedTIContext, EmbeddedTIContextType } from './context';

const YILU_POST_MESSAGE = 'YILU_POST_MESSAGE';

const EXPECTED_MESSAGES = {
  INITIALISE_EMBEDDED_TI: 'INITIALISE_EMBEDDED_TI',
};

const sendPostMessage = (eventType: EmbeddedEventType, data?: any) => {
  const yiluEnvVariables = yiluEnv.getVariables();
  const allowedHostsString = yiluEnvVariables.YILU_MEILI_ALLOWED_HOSTS;
  const allowedHosts = allowedHostsString?.split(',');

  if (allowedHosts && allowedHosts?.length > 0) {
    if (window && window.parent) {
      for (const allowedHost of allowedHosts) {
        console.info('transmitted message for', allowedHost);
        window.parent.postMessage(
          {
            type: YILU_POST_MESSAGE,
            eventType,
            data,
          },
          allowedHost,
        );
      }
    }
  } else {
    console.warn('No allowed hosts list provided, unable to transmit messages');
  }
};

export const EmbeddedTIContextProvider: React.FC = ({ children }) => {
  const navigate = useNavigate();

  useEffect(() => {
    // Fire "EMBEDDED_TI_LOADED" event when the provider loads
    sendPostMessage(EmbeddedEventType.EMBEDDED_TI_LOADED);
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === EXPECTED_MESSAGES.INITIALISE_EMBEDDED_TI) {
        // After parsing the data we can figure out the proper booking flow.
        // For now we know we only support Meili booking flow in the embedded mode.
        const decodedData = decodeBase64(event.data.data);
        // Apply locale from postMessage to i18n before navigating
        if (decodedData?.searchParams?.locale) {
          configureLocale(decodedData.searchParams.locale);
        }
        navigate(routes.MEILI_CAR_RENTAL, { replace: true, state: decodedData });
      }
    };

    // NOTE: This is just a placehodler. Resizing needs to be tested & properly configured ( if needed )
    const onResizeWindow = () => {
      sendPostMessage(EmbeddedEventType.EMBEDDED_TI_RESIZE);
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener('resize', onResizeWindow);

    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('resize', onResizeWindow);
    };
  }, [navigate]);

  const providerValue: EmbeddedTIContextType = { sendPostMessage };

  return <EmbeddedTIContext.Provider value={providerValue}>{children}</EmbeddedTIContext.Provider>;
};
